// v1.8 P0: 10 阶段订单进度条
// 移动端竖排 (timeline), 桌面端横排 (stepper)
// 单源: apps/web/src/lib/workflow.ts (WORKFLOW_STAGES + deriveCurrentStage)
import type { OrderStatus } from '../lib/api';
import { WORKFLOW_STAGES, deriveCurrentStage, getNextHint, stageProgress } from '../lib/workflow';
import { Icon } from './Icon';

interface WorkflowProgressProps {
  status: OrderStatus;
  completedSteps: number;
  totalSteps: number;
  cancelled?: boolean;
}

export function WorkflowProgress({
  status,
  completedSteps,
  totalSteps,
  cancelled = false,
}: WorkflowProgressProps) {
  const current = cancelled ? -1 : deriveCurrentStage(status, completedSteps, totalSteps);
  const overallPct = cancelled ? 0 : stageProgress(current);
  const nextHint = cancelled ? '订单已取消' : getNextHint(current);

  return (
    <div className="bg-surface border border-line rounded p-5 mb-4">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <Icon name="map-pin" size="sm" className="text-accent" />
            订单进度
          </h2>
          <p className="text-xs text-ink-3 mt-0.5">
            {cancelled ? (
              <span className="text-bad">已取消</span>
            ) : (
              <>
                当前阶段:{' '}
                <span className="font-medium text-ink-1">
                  {current + 1}. {WORKFLOW_STAGES[current]?.labelZh}
                </span>{' '}
                · 整体 {overallPct}%
              </>
            )}
          </p>
        </div>
        <div className="shrink-0 text-xs text-ink-3 whitespace-nowrap">
          {completedSteps}/{totalSteps} 步
        </div>
      </div>

      {/* 移动端: 竖排 timeline (< md) */}
      <ol className="md:hidden space-y-1.5">
        {WORKFLOW_STAGES.map((stage) => {
          const isDone = stage.index < current;
          const isCurrent = stage.index === current;
          return (
            <li key={stage.id} className="flex items-start gap-2 text-xs">
              <div
                className={`shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium ${
                  isDone
                    ? 'bg-good text-white'
                    : isCurrent
                      ? 'bg-accent text-white animate-pulse-dot'
                      : 'bg-surface-2 text-ink-3 border border-line'
                }`}
              >
                {isDone ? <Icon name="check" size="xs" strokeWidth={2.5} /> : stage.index + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className={`font-medium ${isCurrent ? 'text-ink-1' : isDone ? 'text-ink-2' : 'text-ink-3'}`}
                >
                  {stage.labelZh}
                </div>
                {isCurrent && <div className="text-ink-3 mt-0.5 leading-snug">{stage.descZh}</div>}
              </div>
            </li>
          );
        })}
      </ol>

      {/* 桌面端: 横排 stepper (≥ md) */}
      <div className="hidden md:block">
        <div className="relative">
          {/* 进度连线 */}
          <div className="absolute top-2 left-2 right-2 h-0.5 bg-line" />
          <div
            className="absolute top-2 left-2 h-0.5 bg-accent transition-all"
            style={{
              width: `calc((100% - 16px) * ${current / (WORKFLOW_STAGES.length - 1)})`,
            }}
          />
          <ol className="relative flex justify-between">
            {WORKFLOW_STAGES.map((stage) => {
              const isDone = stage.index < current;
              const isCurrent = stage.index === current;
              return (
                <li
                  key={stage.id}
                  className="flex flex-col items-center text-center w-0 flex-1"
                  title={stage.descZh}
                >
                  <div
                    className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-medium z-10 ${
                      isDone
                        ? 'bg-good text-white'
                        : isCurrent
                          ? 'bg-accent text-white ring-2 ring-accent/30'
                          : 'bg-surface-2 text-ink-3 border border-line'
                    }`}
                  >
                    {isDone ? <Icon name="check" size="xs" strokeWidth={2.5} /> : stage.index + 1}
                  </div>
                  <div
                    className={`mt-1.5 text-[10px] leading-tight px-0.5 ${
                      isCurrent ? 'font-semibold text-ink-1' : isDone ? 'text-ink-2' : 'text-ink-3'
                    }`}
                  >
                    {stage.labelZh}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      {/* 下一步提示 */}
      {nextHint && (
        <div className="mt-3 text-xs bg-info/10 border border-info/30 rounded p-2.5 leading-relaxed">
          <span className="text-info font-medium">下一步 ·</span> <span>{nextHint}</span>
        </div>
      )}
    </div>
  );
}
